/**
 * Map Firebase Storage download URLs for portal-served objects to same-origin /hosted/… URLs so
 * links display as clients.lctimberframes.com/hosted/… instead of firebasestorage.googleapis.com.
 * Requires Hosting rewrite to Cloud Function `hostedPortalAsset` (see firebase.json).
 *
 * Allowed storage paths: `portal-files/`, `engagement-letters/`, `media-library/`,
 * `portal-branding/`, and Project Hub 3D HTML under `projectFiles/` (`.html`/`.htm` only).
 *
 * Optional query `?n=<display name>` is read by `hostedPortalAsset` to set Content-Disposition on the response.
 * Keep `HOSTED_PORTAL_STORAGE_PREFIXES` in sync with `hostedPortalAsset` in functions/src/index.ts.
 */

const HOSTED_PORTAL_STORAGE_PREFIXES = [
  'portal-files/',
  'engagement-letters/',
  'media-library/',
  'portal-branding/',
] as const;

/** Project Hub 3D HTML uploads (`projectFiles/{projectId}/…`) — HTML/HTM only (not PDFs/quotes). */
function isHostedProjectHubHtmlPath(path: string): boolean {
  if (!path.startsWith('projectFiles/')) return false;
  const ext = (path.split('.').pop() || '').toLowerCase();
  return ext === 'html' || ext === 'htm';
}

function isHostedPortalStoragePath(path: string): boolean {
  if (HOSTED_PORTAL_STORAGE_PREFIXES.some((p) => path.startsWith(p))) return true;
  return isHostedProjectHubHtmlPath(path);
}

/** Encode a Storage object path for a `/hosted/…` URL (path-segment style). */
export function encodeHostedStoragePath(path: string): string {
  return path.split('/').map((seg) => encodeURIComponent(seg)).join('/');
}

/** Decode storage path from `/hosted/…` pathname (legacy %2F or path-segment URLs). */
function decodeHostedPathnameSegment(raw: string): string {
  try {
    if (raw.includes('/')) {
      return raw
        .split('/')
        .map((seg) => decodeURIComponent(seg.replace(/\+/g, '%20')))
        .join('/');
    }
    return decodeURIComponent(raw.replace(/\+/g, '%20'));
  } catch {
    return raw;
  }
}

/** Extract object path from a Firebase Storage download URL, or null if not recognized. */
export function parseFirebaseStoragePathFromDownloadUrl(url: string): string | null {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.pathname.startsWith('/hosted/')) {
      const seg = u.pathname.slice('/hosted/'.length);
      if (!seg) return null;
      return decodeHostedPathnameSegment(seg);
    }
    const m = u.pathname.match(/\/o\/([^?]+)/);
    if (!m?.[1]) return null;
    const encoded = m[1];
    try {
      return decodeURIComponent(encoded.replace(/\+/g, '%20'));
    } catch {
      return encoded;
    }
  } catch {
    return null;
  }
}

/** Human-readable label (e.g. for shop kit row or tooltips). */
export function portalAssetDisplayLabel(downloadUrl: string): string {
  const path = parseFirebaseStoragePathFromDownloadUrl(downloadUrl);
  if (path && isHostedPortalStoragePath(path)) {
    return `clients.lctimberframes.com/hosted/${path}`;
  }
  try {
    const u = new URL(downloadUrl);
    const p = u.pathname + u.search;
    return `${u.hostname}${p.length > 72 ? `${p.slice(0, 72)}…` : p}`;
  } catch {
    return downloadUrl.length > 80 ? `${downloadUrl.slice(0, 80)}…` : downloadUrl;
  }
}

const PORTAL_HOSTED_ASSET_ORIGIN = 'https://clients.lctimberframes.com';

/** Stay under Cloud Functions 10MB streaming cap; larger HTML uses direct Storage URLs in preview. */
export const HOSTED_PORTAL_PROXY_MAX_BYTES = 9 * 1024 * 1024;

/** Origin for `/hosted/…` links — production portal hosts the proxy; dev uses that origin too. */
function hostedAssetOrigin(): string {
  if (typeof window === 'undefined') return PORTAL_HOSTED_ASSET_ORIGIN;
  const h = window.location.hostname;
  if (h === 'clients.lctimberframes.com') return window.location.origin;
  return PORTAL_HOSTED_ASSET_ORIGIN;
}

export type PortalHostedAssetOptions = {
  /**
   * Suggested client-visible filename (basename only). Passed as `?n=` to `hostedPortalAsset`
   * so the proxied response uses Content-Disposition instead of the raw storage path name.
   */
  downloadName?: string | null;
};

/**
 * Hosted href for portal Storage files. Falls back to the original URL for non-portal paths.
 * On localhost, links target clients.lctimberframes.com/hosted/… (production proxy).
 */
export function portalHostedAssetHref(downloadUrl: string, options?: PortalHostedAssetOptions): string {
  if (!downloadUrl.trim()) return downloadUrl;
  try {
    const u = new URL(downloadUrl);
    if (u.pathname.startsWith('/hosted/')) {
      if (u.hostname === 'clients.lctimberframes.com') return downloadUrl;
      const path = u.pathname.slice('/hosted/'.length) + u.search;
      return `${hostedAssetOrigin()}/hosted/${path}`;
    }
  } catch {
    /* fall through */
  }
  const path = parseFirebaseStoragePathFromDownloadUrl(downloadUrl);
  if (!path || !isHostedPortalStoragePath(path)) return downloadUrl;
  let href = `${hostedAssetOrigin()}/hosted/${encodeHostedStoragePath(path)}`;
  const dn = options?.downloadName?.trim();
  if (dn) {
    href += `?n=${encodeURIComponent(dn)}`;
  }
  return href;
}

/**
 * Hosted href for inline HTML / 3D preview. Large files skip the proxy and load from Storage
 * directly so iframe navigation is not truncated by Cloud Functions response limits.
 */
export function portalHtmlPreviewHref(
  downloadUrl: string,
  options?: PortalHostedAssetOptions & { sizeBytes?: number | null },
): string {
  const size = options?.sizeBytes;
  if (typeof size === 'number' && Number.isFinite(size) && size > HOSTED_PORTAL_PROXY_MAX_BYTES) {
    return downloadUrl;
  }
  return portalHostedAssetHref(downloadUrl, options);
}
